import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import './PrivacyPage.css';

const PrivacyPage = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isLoggedIn = !!user && !!user.id;
    const userName = user.full_name || '';

    return (
        <div className="privacy-page-container">
            <SEO 
                title="Privacy Policy & Security Standards"
                description="Read the Smart Interview privacy guidelines, database isolation multi-tenancy models, AES-256 GCM encryption rules, and data handling protocols."
                keywords="privacy policy, tenant isolation, security compliant, database security, encryption standard"
            />
            <Navbar isLoggedIn={isLoggedIn} userName={userName} />

            <main className="privacy-content-wrapper">
                <div className="privacy-card">
                    <span className="privacy-badge">Security Policy</span>
                    <h1>Privacy Policy</h1>
                    <p className="privacy-date">Last Updated: June 1, 2026</p>

                    <div className="privacy-divider"></div>

                    <div className="privacy-body">
                        <section>
                            <h2>1. Overview</h2>
                            <p>
                                Smart Interview ("we", "us", or "our") values the privacy of our clients (recruiters, HR coordinators, administrators) and candidates participating in interviews. This Privacy Policy details how we handle, process, and secure information collected on our platform.
                            </p>
                        </section>

                        <section>
                            <h2>2. Tenant Database Isolation Architecture</h2>
                            <p>
                                Unlike traditional shared database architectures, our platform operates on an enterprise-grade **Isolated Database Multi-Tenancy** structure:
                            </p>
                            <ul>
                                <li><strong>Absolute Separation:</strong> Each registered organization receives a dedicated, isolated database container instance (e.g. SQLite database file or MySQL schema).</li>
                                <li><strong>Zero Inter-tenant Leakage:</strong> Candidate resumes, interview transcripts, recordings, and recruiter lists reside strictly inside your organization's database boundaries, completely isolated from other companies.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>3. Data Encryption Standards</h2>
                            <p>
                                We prioritize data security by encrypting sensitive candidate and support request records:
                            </p>
                            <ul>
                                <li><strong>AES-256-GCM Hashing:</strong> Candidate emails, phone numbers, and support request contact details are stored in an encrypted format using AES-256-GCM.</li>
                                <li><strong>Restricted Decryption:</strong> Data can only be decrypted at runtime by authorized platform services when displayed inside the secure, logged-in recruiter dashboard.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>4. Information We Collect</h2>
                            <p>
                                We collect the following categories of information to conduct and analyze interviews:
                            </p>
                            <ul>
                                <li><strong>Account Details:</strong> Admin name, organization name, work email, and contact details during organization signup.</li>
                                <li><strong>Candidate Submissions:</strong> Resumes (PDF, DOCX), emails, and contact fields.</li>
                                <li><strong>Interview Sessions:</strong> Real-time WebRTC audio/video feeds, screen captures, speech-to-text transcripts, and proctoring activity logs (such as tab focus status).</li>
                                <li><strong>Support Requests:</strong> Support contact details and messages sent via our public demo forms.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>5. Third-Party Integrations</h2>
                            <p>
                                We leverage select sub-processors to conduct key features:
                            </p>
                            <ul>
                                <li><strong>LiveKit:</strong> Powers low-latency real-time video/audio calling.</li>
                                <li><strong>Azure OpenAI:</strong> Processes text-transcripts of audio recordings to generate behavioral matches, screening rubrics, and fraud detections.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>6. Contact Us</h2>
                            <p>
                                If you have questions regarding this Privacy Policy, database isolation, or key rotations, please contact our support team at our support page.
                            </p>
                        </section>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default PrivacyPage;
